import {
  addDecimals,
  compareDecimalStrings,
  isDecimalString,
  subtractDecimals,
} from "@/shared/utils/decimal";

import { PROVIDER_DISPLAY_NAMES } from "../constants/wallets.constants";
import type { ProviderFloat, RuntimeWalletCurrencySummary } from "../types/payments-wallets.types";

/** A reading worth adding up: fresh, or the last good one when a refresh failed. */
export function hasAmount(float: ProviderFloat): float is ProviderFloat & { available: string } {
  return (
    (float.status === "OK" || float.status === "STALE") &&
    float.available !== null &&
    isDecimalString(float.available)
  );
}

export function providerName(code: string): string {
  return PROVIDER_DISPLAY_NAMES[code.toUpperCase()] ?? code;
}

export interface ProviderGroup {
  providerCode: string;
  floats: ProviderFloat[];
  /** Readable amount per currency, in currency order. */
  totals: { currency: string; amount: string }[];
}

/** Floats grouped by provider (by name), payouts before collections, then by rail. */
export function groupFloatsByProvider(floats: ProviderFloat[]): ProviderGroup[] {
  const groups = new Map<string, ProviderFloat[]>();
  for (const float of floats) {
    const list = groups.get(float.provider_code) ?? [];
    list.push(float);
    groups.set(float.provider_code, list);
  }
  return [...groups.entries()]
    .map(([providerCode, list]) => ({
      providerCode,
      floats: [...list].sort(
        (a, b) =>
          a.currency.localeCompare(b.currency) ||
          (a.direction === b.direction ? 0 : a.direction === "PAYOUT" ? -1 : 1) ||
          a.rail.localeCompare(b.rail),
      ),
      totals: totalsByCurrency(list),
    }))
    .sort((a, b) => providerName(a.providerCode).localeCompare(providerName(b.providerCode)));
}

/** Sum of the readable floats per currency. A currency whose floats are all unreadable is left out. */
export function totalsByCurrency(floats: ProviderFloat[]): { currency: string; amount: string }[] {
  const totals = new Map<string, string>();
  for (const float of floats) {
    if (!hasAmount(float)) continue;
    totals.set(float.currency, addDecimals(totals.get(float.currency) ?? "0", float.available));
  }
  return [...totals.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

export interface CoverageRow {
  currency: string;
  /** What Coloca must back (available + blocked), from the wallets summary. */
  held: string;
  /** Readable provider floats in the currency; null when none could be read. */
  inProviders: string | null;
  /** `inProviders - held`: negative is a shortfall. Null without a reading. */
  difference: string | null;
  /** `inProviders / held`; null without a reading or with nothing to back. */
  ratio: number | null;
  /** Some float in this currency could not be read, so `inProviders` may be short. */
  partial: boolean;
}

/**
 * Held against what the providers report, per currency: every currency with something to back, plus any
 * currency a provider holds. Display only for the ratio; sums and differences stay exact.
 */
export function coverageRows(
  currencies: RuntimeWalletCurrencySummary[],
  floats: ProviderFloat[],
): CoverageRow[] {
  const held = new Map(
    currencies
      .filter((row) => isDecimalString(row.held) && compareDecimalStrings(row.held, "0") > 0)
      .map((row) => [row.currency, row.held]),
  );
  const readable = new Map(totalsByCurrency(floats).map((row) => [row.currency, row.amount]));
  const partial = new Set(
    floats.filter((float) => !hasAmount(float) && float.status !== "UNSUPPORTED").map((f) => f.currency),
  );
  const codes = [...new Set([...held.keys(), ...readable.keys()])].sort();

  return codes.map((currency) => {
    const owed = held.get(currency) ?? "0";
    const inProviders = readable.get(currency) ?? null;
    const owesSomething = compareDecimalStrings(owed, "0") > 0;
    return {
      currency,
      held: owed,
      inProviders,
      difference: inProviders === null ? null : subtractDecimals(inProviders, owed),
      ratio: inProviders === null || !owesSomething ? null : Number(inProviders) / Number(owed),
      partial: partial.has(currency),
    };
  });
}
